// Bonk Bomb (§3.3). A BOMB entity flies a ballistic arc that lands on its target after
// `flightTicks`; the first landing lights a `fuseTicks` fuse (`data2` = 1 lit, `timer` = fuse
// left). It can be grabbed (`data` = carrier slot) and re-thrown while the fuse keeps running, and
// explodes wherever it is: on the ground, in the air or in a hand. A bomb that comes down over the
// void is lost with a sploosh (BOMB_LAND with d = 1).
//
// Predict mode (§18.2): only the ballistic flight advances; landing effects, the fuse and the
// explosion are server-only, and no bomb is spawned.
import { DT, NO_SLOT, TUNE, VOID } from '../constants';
import { EV, emit } from '../events';
import { Cause, EK } from '../ids';
import { MAPS } from '../maps/index';
import { clamp } from '../math';
import type { Bean, Entity, World } from '../types';
import { applyBlastAt } from './combat';
import { dcos, dsin } from './dmath';
import { removeEntity, spawnEntity } from './entities';
import { isPredicting } from './world';

/** `Entity.data2` of a lit bomb. */
export const BOMB_LIT = 1;
/** A bomb that sank this far below the arena floor never lands again. */
const SUNK_Z = -TUNE.bean.airborneZ;
/** Aim vectors longer than this carry a target offset in meters (in-process inputs); unit vectors are directions only. */
const UNIT_AIM = 1 + 1e-6;

/**
 * Sets a ballistic arc from (x, y, z) that lands on the ground at (tx, ty) after exactly
 * `flightTicks` (the ground height there, or the floor plane over void), under the round's gravity.
 */
export function launchBomb(w: World, e: Entity, x: number, y: number, z: number, tx: number, ty: number): void {
  const g = MAPS[w.cfg.mapId].groundAt(w, tx, ty);
  const gz = g === VOID ? 0 : g;
  const t = TUNE.bomb.flightTicks * DT;
  e.pos.x = x;
  e.pos.y = y;
  e.z = z;
  e.vel.x = (tx - x) / t;
  e.vel.y = (ty - y) / t;
  e.vz = (gz - z + 0.5 * w.rules.gravity * t * t) / t;
}

/**
 * Item use (§3.3): throws a new bomb in an arc to the aim point. An aim vector longer than 1 is an
 * offset in meters (bots and offline sessions) and is clamped to 3–8 m; a unit aim (the wire
 * normalizes aim, §20.3) or no aim throws the default 6 m along the aim or facing.
 */
export function throwBombItem(w: World, b: Bean, ax: number, ay: number): void {
  if (isPredicting(w)) return;
  const k = TUNE.bomb;
  const l = Math.hypot(ax, ay);
  const ux = l > 0 ? ax / l : dcos(b.facing);
  const uy = l > 0 ? ay / l : dsin(b.facing);
  const range = l > UNIT_AIM ? clamp(l, k.minRange, k.maxRange) : k.defaultRange;
  const z = b.z + TUNE.grab.holdZ;
  const e = spawnEntity(w, EK.BOMB, b.slot, b.pos.x, b.pos.y, z);
  e.data = NO_SLOT;
  launchBomb(w, e, b.pos.x, b.pos.y, z, b.pos.x + ux * range, b.pos.y + uy * range);
}

function explode(w: World, e: Entity): void {
  const k = TUNE.bomb;
  const { x, y } = e.pos;
  const z = e.z;
  emit(w, EV.BOMB_BOOM, e.owner, NO_SLOT, e.id, 0, x, y);
  const carrier = e.data;
  removeEntity(w, e.id);
  applyBlastAt(w, x, y, z, k.radius, k.kbCenter, k.kbEdge, k.dizzy, Cause.BOMB, e.owner, k.maxDz, carrier);
}

/** Entity phase for a BOMB: ballistic flight (unless carried), landing and lighting, the fuse and the explosion. */
export function stepBomb(w: World, e: Entity): void {
  const server = !isPredicting(w);
  if (e.data === NO_SLOT) {
    const map = MAPS[w.cfg.mapId];
    const g = map.groundAt(w, e.pos.x, e.pos.y);
    const resting = g !== VOID && e.z <= g && e.vz === 0 && e.vel.x === 0 && e.vel.y === 0;
    if (!resting) {
      const z0 = e.z;
      const grav = w.rules.gravity;
      e.z += e.vz * DT - 0.5 * grav * DT * DT;
      e.vz -= grav * DT;
      e.pos.x += e.vel.x * DT;
      e.pos.y += e.vel.y * DT;
      const g2 = map.groundAt(w, e.pos.x, e.pos.y);
      if (g2 !== VOID && e.z <= g2 + 1e-9 && e.vz <= 0 && z0 >= g2 + SUNK_Z) {
        e.z = g2;
        e.vz = 0;
        e.vel.x = e.vel.y = 0;
        if (server) {
          emit(w, EV.BOMB_LAND, NO_SLOT, NO_SLOT, e.id, 0, e.pos.x, e.pos.y);
          if (e.data2 !== BOMB_LIT) {
            // The fuse starts on landing and runs `fuseTicks` from the next tick on.
            e.data2 = BOMB_LIT;
            e.timer = TUNE.bomb.fuseTicks;
            return;
          }
        }
      } else if (e.z <= TUNE.edge.killZ) {
        if (server) {
          emit(w, EV.BOMB_LAND, NO_SLOT, NO_SLOT, e.id, 1, e.pos.x, e.pos.y);
          removeEntity(w, e.id);
        }
        return;
      }
    }
  }
  if (server && e.data2 === BOMB_LIT && --e.timer <= 0) explode(w, e);
}
